<template>
  <v-container>
    <div class="d-flex align-center mb-6">
      <v-btn-toggle v-model="statusFilter" mandatory class="mr-4">
        <v-btn value="unsolved" text>
          未練習
        </v-btn>
        <v-btn value="solved" text>
          已練習
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- 科別分類與病患列表 -->
    <div v-if="statusFilter === 'unsolved'">
      <div v-for="(department, index) in patientsCategory" :key="index">
        <div class="d-flex align-center my-4">
          <div class="text-h6 font-weight-bold text-left">{{ department.categoryName }}</div>
          <v-divider class="flex-grow-1 ml-2"></v-divider>
        </div>

        <v-row>
          <v-col v-for="patient in department.patients" :key="patient._id" cols="12" sm="6" md="3">
            <v-card class="patient-card" dark @click="startPractice(patient)">
              <div class="patient-card-background" :style="{
                backgroundImage: `url(${returnImageUrl(patient.backgroundImage)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: 0.5
              }"></div>
              <v-card-text class="patient-card-content">
                <div class="patient-info-card">
                  <div class="text-h6">{{ patient.name }}</div>
                  <div>{{ patient.age }}歲, {{ patient.gender }}</div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </div>

    <div v-else>
      <div v-for="(department, index) in solvedPatientResultData" :key="index">
        <div class="d-flex align-center my-4">
          <div class="text-h6 font-weight-bold text-left">{{ department.name }}</div>
          <v-divider class="flex-grow-1 ml-2"></v-divider>
        </div>
        <v-row>
          <v-col v-for="patient in department.patients" :key="patient._id" cols="12" sm="6" md="3">
            <v-card class="patient-card" dark @click="showPracticeResultsDialog(patient)">
              <div class="patient-card-background" :style="{
                backgroundImage: `url(${returnImageUrl(patient.backgroundImage)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: 0.5
              }"></div>
              <v-card-text class="patient-card-content">
                <div class="patient-info">
                  <div class="text-h6">{{ patient.name }}</div>
                  <div>{{ patient.age }}歲, {{ patient.gender }}</div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- 練習對話框 -->
    <v-dialog v-model="showExamDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card class="exam-dialog">
        <div class="progress-container">
          <v-btn icon @click="previousStep" v-if="currentStep !== 1">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-btn @click="cancelPractice" color="black" dark v-if="currentStep === 1">
            取消練習
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text large :class="{ 'active': currentStep === 1 }" class="mr-2 progress-btn" @click="currentStep = 1">
            面診
          </v-btn>
          <v-btn v-if="currentPatient.firstVisitQuestionExist" text :class="{ 'active': currentStep === 2 }" large class="mr-2 progress-btn" @click="currentStep = 2">
            初診
          </v-btn>
          <v-btn v-if="currentPatient.postOpQuestionExist" text :class="{ 'active': currentStep === 3 }" large class="mr-2 progress-btn" @click="currentStep = 3">
            術後
          </v-btn>
          <v-spacer></v-spacer>
          <template v-if="currentStep === 1">
            <v-btn v-if="currentPatient.firstVisitQuestionExist || currentPatient.postOpQuestionExist" icon @click="nextStep">
              <v-icon>mdi-arrow-right</v-icon>
            </v-btn>
            <v-btn v-else @click="finishPractice" color="black" dark>完成練習</v-btn>
          </template>
          <template v-else-if="currentStep === 2">
            <v-btn v-if="currentPatient.postOpQuestionExist" icon @click="nextStep">
              <v-icon>mdi-arrow-right</v-icon>
            </v-btn>
            <v-btn v-else @click="finishPractice" color="black" dark>完成練習</v-btn>
          </template>
          <template v-else>
            <v-btn @click="finishPractice" color="black" dark>完成練習</v-btn>
          </template>
        </div>

        <v-card-text>
          <!-- 面診階段 -->
          <div v-show="currentStep === 1" class="content-container">
            <!-- 左側：虛擬人影片 -->
            <div class="patient-section">
              <div class="video-section">
                <div class="video-placeholder">
                  <video 
                    id="avatarVideo" 
                    autoplay 
                    playsinline
                    class="avatar-video"
                    @loadeddata="onAvatarVideoLoaded"
                    style="width: 100%; height: 100%; object-fit: cover;"
                  ></video>
                  <audio 
                    id="avatarAudio" 
                    autoplay 
                    style="display: none;"
                    controls
                    volume="1.0"
                  ></audio>
                  <div v-if="!isAvatarVideoLoaded" class="video-loading-text">載入中...</div>
                  <div v-if="isRTCConnecting" class="rtc-loading-overlay">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                    <div class="rtc-loading-tip">正在建立連線，請稍候...</div>
                  </div>
                </div>
                <div class="test-controls mt-4">
                  <v-btn
                    color="primary"
                    @click="testAvatarSpeak"
                    :loading="isSpeaking"
                    :disabled="!isAvatarReady"
                  >
                    {{ isSpeaking ? '說話中...' : '測試說話' }}
                  </v-btn>
                  <v-slider
                    v-model="audioVolume"
                    class="mt-2"
                    min="0"
                    max="1"
                    step="0.1"
                    @input="updateAudioVolume"
                    prepend-icon="mdi-volume-high"
                    style="max-width: 200px;"
                  ></v-slider>
                </div>
              </div>
            </div>

            <!-- 右側：病患資料、圖片和聊天室 -->
            <div class="exam-form-section">
              <!-- 上半部：病患資料和圖片 -->
              <div class="top-section">
                <!-- 左側：病患資料 -->
                <div class="patient-info-section">
                  <div class="d-flex justify-space-between mb-2">
                    <div class="info-group">
                      <div class="info-label text-left">姓名</div>
                      <v-text-field v-model="currentPatient.name" outlined readonly hide-details></v-text-field>
                    </div>
                    <div class="info-group">
                      <div class="info-label text-left">性別</div>
                      <v-text-field v-model="currentPatient.gender" outlined readonly hide-details></v-text-field>
                    </div>
                    <div class="info-group">
                      <div class="info-label text-left">年齡</div>
                      <v-text-field v-model="currentPatient.age" outlined readonly hide-details></v-text-field>
                    </div>
                  </div>
                  <div class="chief-complaint">
                    <div class="info-label text-left">情境說明</div>
                    <v-textarea v-model="currentPatient.description" outlined readonly hide-details rows="4"></v-textarea>
                    <div class="info-label text-left">任務說明</div>
                    <v-textarea v-model="currentPatient.taskDescription" outlined readonly hide-details rows="4"></v-textarea>
                  </div>
                </div>

                <!-- 右側：圖片 -->
                <div class="image-section_face">
                  <div class="d-flex align-center mb-2">
                    <span class="subtitle-1 font-weight-bold">圖片</span>
                  </div>
                  <div class="image-slider-container_face">

                    <!-- <v-btn v-if="currentPatient.clinicFiles.length > 2" icon class="slider-arrow left" @click="scrollImages('left')">
                      <v-icon>mdi-chevron-left</v-icon>
                    </v-btn> -->
                    <div class="image-scroll-container_face" ref="imageContainer2" style="display: flex; flex-direction: column-reverse;">
                      <div class="image-wrapper_face" v-for="image in currentPatient.clinicFiles" :key="image">
                        <v-img :src="returnClinicFileUrl(image)" height="100%" class="rounded-lg cursor-pointer" @click="openImageDialog(returnClinicFileUrl(image))"></v-img>
                      </div>
                    </div>
                    <!-- <v-btn v-if="currentPatient.clinicFiles.length > 2" icon class="slider-arrow right" @click="scrollImages('right')">
                      <v-icon>mdi-chevron-right</v-icon>
                    </v-btn> -->
                  </div>
                </div>
              </div>

              <!-- 下半部：聊天室 -->
              <div class="chat-section">
                <div class="chat-container">
                  <div class="messages-container">
                    <div v-for="message in messages" :key="message._id" :class="['message', message.sender_id === currentUserId ? 'message-sent' : 'message-received']">
                      {{ message.content }}
                    </div>
                    <div v-if="isWaiting" class="message message-received typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                  <div class="input-container">
                    <v-text-field v-model="messageInput" placeholder="請輸入內容..." outlined dense hide-details @keyup.enter="handleSendMessage">
                      <template v-slot:append>
                        <v-btn icon small @click="handleSendMessage">
                          <v-icon>mdi-send</v-icon>
                        </v-btn>
                        <v-btn icon small @click="handleVoiceInput('messageInput')" :class="{ 'recording': isRecording, 'processing': isProcessing }" :disabled="isProcessing">
                          <v-icon>{{ isRecording ? 'mdi-microphone-off' : 'mdi-microphone' }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 初診階段 -->
          <div v-if="currentStep === 2" class="content-container">
            <div class="patient-section">
              <v-card class="pa-4">
                <div class="mb-4">
                  <div class="subtitle-1 font-weight-bold mb-2">姓名</div>
                  <v-text-field v-model="currentPatient.name" outlined dense readonly></v-text-field>
                </div>
                <div class="mb-4">
                  <div class="subtitle-1 font-weight-bold mb-2">性別</div>
                  <v-text-field v-model="currentPatient.gender" outlined dense readonly></v-text-field>
                </div>
                <div class="mb-4">
                  <div class="subtitle-1 font-weight-bold mb-2">年齡</div>
                  <v-text-field v-model="currentPatient.age" outlined dense readonly></v-text-field>
                </div>
                <div class="mb-4">
                  <div class="subtitle-1 font-weight-bold mb-2">情境說明</div>
                  <v-textarea v-model="currentPatient.description" outlined readonly rows="2"></v-textarea>
                </div>
                <div class="mb-4">
                  <div class="subtitle-1 font-weight-bold mb-2">任務說明</div>
                  <v-textarea v-model="currentPatient.taskDescription" outlined readonly rows="2"></v-textarea>
                </div>
                <div class="image-section">
                  <div class="d-flex align-center mb-2">
                    <span class="subtitle-1 font-weight-bold">圖片</span>
                  </div>
                  <div class="image-slider-container">
                    <v-btn v-if="currentPatient.firstVisitFiles.length > 2" icon class="slider-arrow left" @click="scrollImages('left', $event)">
                      <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                    <div class="image-scroll-container" :ref="'imageContainer' + currentStep">
                      <div class="image-wrapper" v-for="image in currentPatient.firstVisitFiles" :key="image">
                        <v-img :src="returnFirstVisitFileUrl(image)" width="180" height="120" cover class="rounded-lg cursor-pointer" @click="openImageDialog(returnFirstVisitFileUrl(image))"></v-img>
                      </div>
                    </div>
                    <v-btn v-if="currentPatient.firstVisitFiles.length > 2" icon class="slider-arrow right" @click="scrollImages('right', $event)">
                      <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-card>
            </div>
            <div class="exam-form-section">
              <v-card class="pa-4">
                <div class="mb-6">
                  <div class="subtitle-1 font-weight-bold mb-4">病患標籤</div>
                  <div class="d-flex flex-wrap">
                    <v-chip v-for="tag in currentPatient.tags" :key="tag._id" outlined color="black" class="ma-2 large-chip" text-color="black">
                      {{ tag.name }}
                    </v-chip>
                  </div>
                </div>
                <div class="questions-answer-container">
                  <div class="questions-list">
                    <div v-for="(question, index) in currentPatient.firstVisitQuestions" :key="index" class="nav-item" :class="{ active: currentFirstVisitQuestionIndex === index }" @click="currentFirstVisitQuestionIndex = index">
                      題目{{ index + 1 }}
                    </div>
                    <div class="nav-arrows">
                      <v-btn icon small @click="previousFirstVisitQuestion">
                        <v-icon>mdi-chevron-left</v-icon>
                      </v-btn>
                      <v-btn icon small @click="nextFirstVisitQuestion">
                        <v-icon>mdi-chevron-right</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <div class="answer-container">
                    <div class="question-title mb-4">問題</div>
                    <div class="question-text mb-6">{{ currentPatient.firstVisitQuestions[currentFirstVisitQuestionIndex].title }}</div>
                    <div class="answer-section">
                      <div class="answer-label mb-2">回答</div>
                      <v-textarea v-model="currentPatient.firstVisitQuestions[currentFirstVisitQuestionIndex].answer" outlined placeholder="請輸入回答內容" rows="8" hide-details @input="saveTesttoLocalStorage"></v-textarea>
                      <v-btn icon class="ml-2" @click="handleVoiceInput('currentPatient.firstVisitQuestions[currentFirstVisitQuestionIndex].answer')">
                        <v-icon>mdi-microphone</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-card>
            </div>
          </div>

          <!-- 術後階段 -->
          <div v-if="currentStep === 3" class="content-container">
            <div class="patient-section">
              <v-card class="pa-4">
                <div class="mb-4">
                  <div class="subtitle-1 font-weight-bold mb-2">姓名</div>
                  <v-text-field v-model="currentPatient.name" outlined dense readonly></v-text-field>
                </div>
                <div class="mb-4">
                  <div class="subtitle-1 font-weight-bold mb-2">性別</div>
                  <v-text-field v-model="currentPatient.gender" outlined dense readonly></v-text-field>
                </div>
                <div class="mb-4">
                  <div class="subtitle-1 font-weight-bold mb-2">年齡</div>
                  <v-text-field v-model="currentPatient.age" outlined dense readonly></v-text-field>
                </div>
                <div class="mb-4">
                  <div class="subtitle-1 font-weight-bold mb-2">情境說明</div>
                  <v-textarea v-model="currentPatient.description" outlined readonly rows="2"></v-textarea>
                </div>
                <div class="mb-4">
                  <div class="subtitle-1 font-weight-bold mb-2">任務說明</div>
                  <v-textarea v-model="currentPatient.taskDescription" outlined readonly rows="2"></v-textarea>
                </div>
                <div class="image-section">
                  <div class="d-flex align-center mb-2">
                    <span class="subtitle-1 font-weight-bold">圖片</span>
                  </div>
                  <div class="image-slider-container">
                    <v-btn v-if="currentPatient.postOpFiles.length > 2" icon class="slider-arrow left" @click="scrollImages('left', $event)">
                      <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                    <div class="image-scroll-container" :ref="'imageContainer' + currentStep">
                      <div class="image-wrapper" v-for="image in currentPatient.postOpFiles" :key="image">
                        <v-img :src="returnPostOpFileUrl(image)" width="180" height="120" cover class="rounded-lg cursor-pointer" @click="openImageDialog(returnPostOpFileUrl(image))"></v-img>
                      </div>
                    </div>
                    <v-btn v-if="currentPatient.postOpFiles.length > 2" icon class="slider-arrow right" @click="scrollImages('right', $event)">
                      <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-card>
            </div>
            <div class="exam-form-section">
              <v-card class="pa-4">
                <div class="mb-6">
                  <div class="subtitle-1 font-weight-bold mb-4">病患標籤</div>
                  <div class="d-flex flex-wrap">
                    <v-chip v-for="tag in currentPatient.tags" :key="tag._id" outlined color="black" class="ma-2 large-chip" text-color="black">
                      {{ tag.name }}
                    </v-chip>
                  </div>
                </div>
                <div class="questions-answer-container">
                  <div class="questions-list">
                    <div v-for="(question, index) in currentPatient.postOpQuestions" :key="index" class="nav-item" :class="{ active: currentPostOpQuestionIndex === index }" @click="currentPostOpQuestionIndex = index">
                      題目{{ index + 1 }}
                    </div>
                    <div class="nav-arrows">
                      <v-btn icon small @click="previousPostOpQuestion">
                        <v-icon>mdi-chevron-left</v-icon>
                      </v-btn>
                      <v-btn icon small @click="nextPostOpQuestion">
                        <v-icon>mdi-chevron-right</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <div class="answer-container">
                    <div class="question-title mb-4">問題</div>
                    <div class="question-text mb-6">{{ currentPatient.postOpQuestions[currentPostOpQuestionIndex].title }}</div>
                    <div class="answer-section">
                      <div class="answer-label mb-2">回答</div>
                      <v-textarea v-model="currentPatient.postOpQuestions[currentPostOpQuestionIndex].answer" outlined placeholder="請輸入回答內容" rows="8" hide-details @input="saveTesttoLocalStorage"></v-textarea>
                      <v-btn icon class="ml-2" @click="handleVoiceInput('currentPatient.postOpQuestions[currentPostOpQuestionIndex].answer')">
                        <v-icon>mdi-microphone</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-card>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 圖片放大對話框 -->
    <v-dialog v-model="showImageDialog">
      <v-card>
        <v-card-actions class="d-flex justify-end pa-2">
          <v-btn icon @click="showImageDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-actions>
        <v-img :src="selectedImage" contain height="100%" width="100%"></v-img>
      </v-card>
    </v-dialog>


    <!-- 錄音對話框 -->
    <v-dialog v-model="showRecordingDialog" persistent max-width="400">
      <v-card>
        <v-card-title class="headline">
          語音輸入中
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="text-center mb-4">
            <v-icon :color="'red'" size="64" class="recording-icon">
              mdi-microphone
            </v-icon>
          </div>
          <div class="text-center mb-4">
            正在錄音...請說話
          </div>
          <div class="text-center mb-4" v-if="isProcessing">
            正在處理語音...
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div class="text-center red--text" v-if="recordingError">
            {{ recordingError }}
          </div>
        </v-card-text>
        <v-card-actions class="pb-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="cancelRecording"
            :disabled="isProcessing"
          >
            取消
          </v-btn>
          <v-btn
            color="red darken-1"
            dark
            @click="toggleRecording"
            :loading="isProcessing"
            :disabled="isProcessing"
          >
            停止錄音
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 等待評分對話框 -->
    <v-dialog v-model="showWaitingDialog" width="300">
      <v-card>
        <v-card-text class="pa-6">
          <div class="d-flex justify-center">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          </div>
          <div class="text-h6 mt-4 text-center">評分中...</div>
          <div class="text-subtitle-1 mt-2 text-center">請稍候</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 新增練習結果對話框 -->
    <v-dialog v-model="showPracticeResults" max-width="1000">
      <v-card>
        <v-card-title class="headline d-flex align-center">
          練習結果列表
          <v-spacer></v-spacer>
          <v-btn color="black" dark @click="exportPracticeResultsCsv" class="mr-2">
            匯出 CSV
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-data-table :headers="practiceHeaders" :items="practiceResults" hide-default-footer class="elevation-0">
            <template v-slot:item="{ item }">
              <tr>
                <td class="text-left">{{ formatDateTime(item.uploadTime) }}</td>
                <td class="text-left">{{ item.student?.name || '未知學生' }}</td>
                <td class="text-left">{{ item.examScore || 0 }}</td>
                <td class="text-center">
                  <v-btn small text color="primary" @click="showAnswerDetail(item)" class="mr-2">
                    <v-icon small class="mr-1">mdi-file-document-outline</v-icon>
                    答題過程
                  </v-btn>
                  <v-btn small text color="primary" @click="viewScoreSheet(item)">
                    <v-icon small class="mr-1">mdi-file-check-outline</v-icon>
                    評分表
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn text @click="showPracticeResults = false">關閉</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 答題過程查看對話框 -->
    <exam-answer-viewer v-model="showAnswerViewer" :exam-result="selectedExamResult" :student-answer="studentAnswer" />

    <v-dialog v-model="showScoreSheet" width="1000">
      <score-sheet 
        :exam-data="examData" 
        @close="handleClose"
        @export-csv="handleExportCSV"
      />
    </v-dialog>

    <!-- 全屏 loading dialog -->
    <v-dialog v-model="showRTCLoading" persistent width="300">
      <v-card>
        <v-card-text class="pa-6">
          <div class="d-flex justify-center">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          </div>
          <div class="text-h6 mt-4 text-center">正在建立連線，請稍候...</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 新增 resume practice 按鈕 -->
    <v-dialog v-model="showResumePracticeBtn" persistent max-width="400">
      <v-card>
        <v-card-title class="headline">繼續練習</v-card-title>
        <v-card-text>
          偵測到你有尚未完成的練習，是否要繼續？
        </v-card-text>
        <v-card-actions>
          <v-btn color="grey" text @click="cancelResumePractice">取消練習</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="resumePractice">繼續練習</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    
  </v-container>
</template>

<script>
import PatientService from '../../services/patient.service'
import ChatService from '../../services/chat.service'
import ExamAnswerService from '../../services/examAnswer.service'
import baseUrl from '../../../baseUrl.config'
import ScoreSheet from '@/components/ScoreSheet.vue'
import ExamResultService from '../../services/examResult.service'
import RecorderService from '../../services/recorder.service'
import AudioService from '../../services/audio.service'
import ExamAnswerViewer from '@/components/ExamAnswerViewer.vue';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk'
import getAvatarPreset from '@/config/avatarPresets'

export default {
  name: 'StudentPatients',

  data() {
    return {
      showWaitingDialog: false,
      statusFilter: 'unsolved',
      showExamDialog: false,
      showImageDialog: false,
      selectedImage: '',
      currentStep: 1,
      currentPatient: {
        name: "",
        gender: "",
        age: "",
        description: "",
        taskDescription: "",
        backgroundImage: "",
        clinicFiles: [],
        firstVisitFiles: [],
        postOpFiles: [],
        firstVisitQuestions: [],
        postOpQuestions: [],
        postOpQuestionsExist: false,
        firstVisitQuestionsExist: false,
        tags: []
      },
      currentUserId: 'student',
      messageInput: '',
      messages: [],
      currentFirstVisitQuestionIndex: 0,
      currentPostOpQuestionIndex: 0,
      patientsCategory: [],
      isWaiting: false,
      solvedPatientResultData: [],
      showRecordingDialog: false,
      isProcessing: false,
      recordingError: '',
      isRecording: false,
      currentRecordingTarget: null,
      examData: {
        exam: {
          name: '',
          duration: 0
        },
        student: {
          name: ''
        },
        examScore: 0,
        examDuration: 0,
        examResult: {
          result: 0,
          comment: ''
        },
        teacher: {
          name: ''
        },
        examResultDetail: []
      },
      showPracticeResults: false,
      practiceResults: [],
      practiceHeaders: [
        { text: '完成時間', value: 'uploadTime', width: '25%' },
        { text: '分數', value: 'examScore', width: '20%' },
        { text: '操作', value: 'actions', sortable: false, width: '30%' }
      ],
      showAnswerViewer: false,
      selectedExamResult: {},
      studentAnswer: {},
      showScoreSheet: false,
      avatarSynthesizer: null,
      peerConnection: null,
      isAvatarReady: false,
      avatarConfig: null,
      speechConfig: null,
      isAzureConfigured: true,
      azureConfig: {
        speechKey: '3lZPCieuOAFJaA2fddPr8Jy4tl2CJoX2FNSa1MJsPl6KAQ8KuZ2sJQQJ99BKACqBBLyXJ3w3AAAAACOG3NfA',
        speechRegion: 'southeastasia',
        iceServerUrl: '',
        iceUsername: '',
        iceCredential: ''
      },
      isSpeaking: false,
      isInitializing: false,
      audioVolume: 1.0,
      isAvatarVideoLoaded: false,
      isRTCConnecting: false,
      showRTCLoading: false,
      avatarVideoStream: null,
      showResumePracticeBtn: false,
    }
  },

  components: {
    ScoreSheet,
    ExamAnswerViewer
  },

  mounted() {
    this.getPracticePatientWithGroupByStudent()
    this.checkSavedPractice()
    this.getPatientResult()
  },

  methods: {
    viewScore(exam) {
      this.examData = {
        exam: {
          name: exam.exam?.name || '練習測驗',
          duration: exam.duration || 0
        },
        student: {
          name: exam.student?.name || localStorage.getItem('name')
        },
        examScore: exam.examScore || 0,
        examDuration: exam.duration || 0,
        examResult: {
          result: exam.result || 0,
          comment: exam.comment || ''
        },
        teacher: {
          name: exam.teacher?.name || '考官'
        },
        examResultDetail: exam.examResultDetail || []
      };
      this.showScoreSheet = true;
    },
    handleExportCSV() {
      console.log('Export to CSV triggered')
    },
    handleClose() {
      this.showScoreSheet = false
    },
    getPatientResult() {
      ExamResultService.studentGetPatientResult(this.examData.examAnswerId).then((res) => {
        this.solvedPatientResultData = res.data.data
      })
    },
    getPracticePatientWithGroupByStudent() {
      PatientService.getPracticePatientWithGroupByStudent().then((res) => {
        this.patientsCategory = res.data.data
      })
    },
    async checkSavedPractice() {
      const savedPatient = localStorage.getItem('currentPatient')
      const savedMessages = localStorage.getItem('messageCache')
      const patientId = localStorage.getItem('patient_id')

      if (savedPatient && patientId) {
        this.currentPatient = JSON.parse(savedPatient)
        if (savedMessages) {
          this.messages = JSON.parse(savedMessages)
        }
        this.showResumePracticeBtn = true;
      }
    },
    async resumePractice() {
      console.log('[LOG] User clicked resumePractice');
      this.showRTCLoading = true;
      this.currentStep = 1;
      await this.initAzureSpeechService();
      this.showExamDialog = true;
      this.showResumePracticeBtn = false;
      console.log('[LOG] resumePractice finished, showExamDialog:', this.showExamDialog);
    },
    saveTesttoLocalStorage() {
      localStorage.setItem('currentPatient', JSON.stringify(this.currentPatient))
      localStorage.setItem('messageCache', JSON.stringify(this.messages))
      localStorage.setItem('patient_id', this.currentPatient._id)
    },
    generateUUID() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                var r = Math.random()*16|0, v = c == 'x' ? r : (r&0x3|0x8);
                return v.toString(16);
            });
        },
    async initAzureSpeechService() {
      if (this.isInitializing) {
        console.log('Initialization already in progress, skipping...');
        return;
      }
      this.isInitializing = true;
      this.isRTCConnecting = true;
      const rtcStart = Date.now();
      let tIceFetchStart, tIceFetchEnd, tPeerStart, tPeerEnd;
      console.log('[RTC] Start initAzureSpeechService, isRTCConnecting:', this.isRTCConnecting);
      try {
        tIceFetchStart = Date.now();
        console.log('[RTC] Creating SpeechConfig...');
        this.speechConfig = SpeechSDK.SpeechConfig.fromSubscription(
          this.azureConfig.speechKey,
          this.azureConfig.speechRegion
        );
        const avatarPreset = getAvatarPreset(
          this.currentPatient.language,
          this.currentPatient.gender
        );
        this.speechConfig.speechSynthesisLanguage = avatarPreset.locale;
        this.speechConfig.speechSynthesisVoiceName = avatarPreset.voice;
        console.log('[RTC] Avatar preset:', avatarPreset);
        console.log('[RTC] SpeechConfig created');
        this.avatarConfig = new SpeechSDK.AvatarConfig(
          avatarPreset.character,
          avatarPreset.style
        );
        // let backgroundImageUrl = "https://osce.djmp.tw/uploadFiles/backgroundImage/"+this.currentPatient.backgroundImage;
        let backgroundImageUrl = "https://osce.djmp.tw/uploadFiles/1.jpg";
        console.log('[Setting] backgroundImageUrl:', backgroundImageUrl);
        this.avatarConfig.backgroundImage = new URL(backgroundImageUrl);
        console.log('[RTC] AvatarConfig created');
        // Fetch ICE server
        console.log('[RTC] Fetching ICE server info...');
        const iceResponse = await fetch('https://' + this.azureConfig.speechRegion + '.tts.speech.microsoft.com/cognitiveservices/avatar/relay/token/v1', {
          headers: {
            'Ocp-Apim-Subscription-Key': this.azureConfig.speechKey
          }
        });
        tIceFetchEnd = Date.now();
        console.log(`[RTC] ICE server fetch took ${tIceFetchEnd - tIceFetchStart} ms`);
        if (!iceResponse.ok) {
          throw new Error(`[RTC] Failed to fetch ICE server info: ${iceResponse.status} ${iceResponse.statusText}`);
        }
        const iceData = await iceResponse.json();
        console.log('[RTC] ICE Data received:', iceData);
        const iceServers = [];
        if (iceData && iceData.Urls && iceData.Urls.length > 0) {
          console.log('[RTC] Adding Azure ICE server configuration');
          iceServers.push({
            urls: iceData.Urls.filter(url => url.startsWith('turn:')),
            username: iceData.Username,
            credential: iceData.Password
          });
        }
        tPeerStart = Date.now();
        console.log('[RTC] Creating RTCPeerConnection with ICE servers:', iceServers);
        this.peerConnection = new RTCPeerConnection({
          iceServers: iceServers,
          iceTransportPolicy: 'all',
          bundlePolicy: 'balanced',
          rtcpMuxPolicy: 'require'
        });
        tPeerEnd = Date.now();
        console.log(`[RTC] RTCPeerConnection creation took ${tPeerEnd - tPeerStart} ms`);
        // Add transceivers
        console.log('[RTC] Adding transceivers...');
        this.peerConnection.addTransceiver('video', { direction: 'sendrecv' });
        this.peerConnection.addTransceiver('audio', { direction: 'sendrecv' });

        // Set up track handling
        this.peerConnection.ontrack = (event) => {
          console.log('[RTC] Track received:', event.track.kind, event.streams);
          if (event.track.kind === 'video') {
            let videoElement = document.getElementById('avatarVideo');
            if (videoElement) {
              console.log('[RTC] Setting video source object', event.streams[0]);
              videoElement.srcObject = event.streams[0];
              videoElement.autoplay = true;
              videoElement.playsInline = true;
              videoElement.onerror = (e) => {
                console.error('[RTC] videoElement error:', e);
              };
            } else {
              this.avatarVideoStream = event.streams[0];
              let retry = 0;
              const trySetVideo = () => {
                const el = document.getElementById('avatarVideo');
                if (el) {
                  el.srcObject = this.avatarVideoStream;
                  el.autoplay = true;
                  el.playsInline = true;
                  el.onerror = (e) => {
                    console.error('[RTC] videoElement error (delayed):', e);
                  };
                  console.log('[RTC] Delayed set videoElement.srcObject');
                } else if (retry < 20) {
                  retry++;
                  setTimeout(trySetVideo, 100);
                }
              };
              trySetVideo();
            }
          } else if (event.track.kind === 'audio') {
            const setAudio = () => {
              const audioElement = document.getElementById('avatarAudio');
              if (audioElement) {
                console.log('[RTC] Setting audio source object');
                audioElement.srcObject = event.streams[0];
                audioElement.autoplay = true;
                audioElement.volume = this.audioVolume;
                audioElement.load();
                // log audio element state before play
                console.log('[RTC] audioElement.srcObject:', audioElement.srcObject);
                console.log('[RTC] audioElement.volume:', audioElement.volume);
                console.log('[RTC] audioElement.muted:', audioElement.muted);
                console.log('[RTC] audioElement.readyState:', audioElement.readyState);
                audioElement.play().then(() => {
                  console.log('[RTC] Audio play() success');
                  setTimeout(() => {
                    console.log('[RTC] After play: volume=' + audioElement.volume + ', muted=' + audioElement.muted + ', readyState=' + audioElement.readyState + ', srcObject=', audioElement.srcObject);
                  }, 500);
                }).catch(error => {
                  console.error('[RTC] Error playing audio:', error);
                  setTimeout(() => {
                    console.log('[RTC] After play error: volume=' + audioElement.volume + ', muted=' + audioElement.muted + ', readyState=' + audioElement.readyState + ', srcObject=', audioElement.srcObject);
                  }, 500);
                });
              } else {
                console.warn('[RTC] audioElement not found, retrying...');
                setTimeout(setAudio, 100);
              }
            };
            setAudio();
          }
        };

        // Monitor connection state
        this.peerConnection.oniceconnectionstatechange = () => {
          console.log('[RTC] ICE connection state changed to:', this.peerConnection.iceConnectionState);
        };

        // Create and start AvatarSynthesizer
        console.log('[RTC] Creating AvatarSynthesizer...');
        this.avatarSynthesizer = new SpeechSDK.AvatarSynthesizer(
          this.speechConfig,
          this.avatarConfig
        );

        console.log('[RTC] Starting avatar...');
        try {
          await this.avatarSynthesizer.startAvatarAsync(this.peerConnection);
          console.log('[RTC] Avatar started successfully');
          this.isAvatarReady = true;
          this.isRTCConnecting = false;
        } catch (error) {
          this.isRTCConnecting = false;
          throw error;
        }
        const rtcEnd = Date.now();
        console.log(`[RTC] Total RTC connection time: ${rtcEnd - rtcStart} ms`);
      } catch (error) {
        this.isAvatarReady = false;
        this.isRTCConnecting = false;
      } finally {
        this.isInitializing = false;
        console.log('[RTC] initAzureSpeechService finished. isRTCConnecting:', this.isRTCConnecting, 'isAvatarReady:', this.isAvatarReady);
      }
    },
    async startPractice(patient) {
      try {
        const res = await PatientService.getPatientByStudent(patient._id);
        if (res.data.data) {
          this.currentPatient = res.data.data;
          this.currentPatient.startTime = new Date();
          this.currentPatient.chat_id = this.generateUUID();

          this.showRTCLoading = true; // 先顯示 loading
          this.currentStep = 1;
          this.saveTesttoLocalStorage();
          console.log('[UI] showRTCLoading = true, start RTC connection');

          await this.initAzureSpeechService(); // 等待 RTC 完成
          this.showExamDialog = true; // 連線好才顯示測驗
          console.log('[UI] showExamDialog = true (loading still on)');
        }
      } catch (error) {
        this.showRTCLoading = false;
        console.error('Error starting practice:', error);
      }
    },
    async handleSendMessage() {
      if (!this.messageInput.trim()) return;
      const message = {
        _id: Date.now(),
        content: this.messageInput,
        sender_id: this.currentUserId,
        timestamp: new Date().getTime(),
        chat: { _id: this.currentPatient.chat_id },
        patient: { _id: this.currentPatient._id },
        student: { _id: localStorage.getItem('user_id') }
      };
      this.messages.push(message);
      this.messageInput = '';
      this.isWaiting = true;
      this.saveTesttoLocalStorage();
      try {
        const res = await ChatService.chat(message);
        const replyMessage = {
          _id: Date.now(),
          content: res.data.response,
          sender_id: 'patient',
          timestamp: new Date().getTime(),
        };
        this.messages.push(replyMessage);
        this.saveTesttoLocalStorage();
        if (this.isAvatarReady && this.avatarSynthesizer) {
          const replyText = replyMessage.content;
          if (replyText && typeof replyText === 'string' && replyText.trim() !== '') {
            await this.avatarSynthesizer.speakTextAsync(replyText);
          } else {
            console.warn('[Avatar] 回覆語音內容為空，略過');
          }
        }
      } catch (error) {
        console.error('Error in chat:', error);
        const errorMessage = {
          _id: Date.now(),
          content: '發生錯誤，請稍後再試',
          sender_id: 'patient',
          timestamp: new Date().getTime()
        };
        this.messages.push(errorMessage);
        this.saveTesttoLocalStorage();
      } finally {
        this.isWaiting = false;
        }
    },
    scrollImages(direction) {
      const container = this.$refs.imageContainer2
      const scrollAmount = 180 + 16 // 圖片寬度 + gap

      if (direction === 'left') {
        container.scrollLeft -= scrollAmount
      } else {
        container.scrollLeft += scrollAmount
      }
    },
    openImageDialog(imageUrl) {
      this.selectedImage = imageUrl
      this.showImageDialog = true
    },
    previousStep() {
      if (this.currentStep === 3) {
        if (this.currentPatient.firstVisitQuestionsExist) {
          this.currentStep = 2;
        } else {
          this.currentStep = 1;
        }
      } else if (this.currentStep === 2) {
        this.currentStep = 1;
      }
    },
    nextStep() {
      if (this.currentStep === 1) {
        if (this.currentPatient.firstVisitQuestionsExist) {
          this.currentStep = 2;
        } else if (this.currentPatient.postOpQuestionsExist) {
          this.currentStep = 3;
        }
      } else if (this.currentStep === 2 && this.currentPatient.postOpQuestionsExist) {
        this.currentStep = 3;
      }
    },

    nextFirstVisitQuestion() {
      if (this.currentFirstVisitQuestionIndex < this.currentPatient.firstVisitQuestions.length - 1) {
        this.currentFirstVisitQuestionIndex++
      }
    },

    nextPostOpQuestion() {
      if (this.currentPostOpQuestionIndex < this.currentPatient.postOpQuestions.length - 1) {
        this.currentPostOpQuestionIndex++
      }
    },

    previousPostOpQuestion() {
      if (this.currentPostOpQuestionIndex > 0) {
        this.currentPostOpQuestionIndex--
      }
    },

    previousFirstVisitQuestion() {
      if (this.currentFirstVisitQuestionIndex > 0) {
        this.currentFirstVisitQuestionIndex--
      }
    },

    finishPractice() {
      this.showWaitingDialog = true
      this.currentPatient.endTime = new Date()
      if (this.currentPatient.startTime) {
        this.currentPatient.duration = (this.currentPatient.endTime - this.currentPatient.startTime) / 1000
      } else {
        this.currentPatient.duration = 0
      }
      this.currentPatient.messages = this.messages

      ExamAnswerService.add(this.currentPatient).then((res) => {
        this.showWaitingDialog = false
        // 解析 serverResult 字串
        if (res.data && res.data.data && res.data.data.serverResult) {
          const serverResult = JSON.parse(res.data.data.serverResult);
          
          // 設置測驗結果數據
          this.examData = {
            exam: {
              name: '練習測驗',
              duration: serverResult.examDuration
            },
            student: {
              name: localStorage.getItem('name')
            },
            examScore: serverResult.examScore,
            examDuration: serverResult.examDuration,
            examResult: {
              result: serverResult.examResult.result,
              comment: serverResult.examResult.comment || ''
            },
            examResultDetail: serverResult.examResultDetail.map(section => ({
              SectionTitle: section.SectionTitle,
              items: Array.isArray(section.items) ? 
                section.items.map(item => ({
                  title: item.title || '',
                  result: typeof item.result === 'string' ? 1 : item.result,
                  comment: item.comment || ''
                })) : []
            })),
            teacher: {
              name: '考官'
            }
          };

          // 顯示評分表
          this.showScoreSheet = true;
        }

        // 關閉練習對話框
        this.showExamDialog = false;
        
        // 清理數據
        this.messages = [];
        this.clearLocalStorage();
        this.getPracticePatientWithGroupByStudent();
      })
      .catch((err) => {
        this.showWaitingDialog = false
        console.error('Error submitting practice:', err);
        alert('提交練習時發生錯誤');
      });
    },

    cancelPractice() {
      this.cleanupAvatar();
      this.currentPatient = {
        name: "",
        gender: "",
        age: "",
        description: "",
        taskDescription: "",
        backgroundImage: "",
        clinicFiles: [],
        firstVisitFiles: [],
        postOpFiles: [],
        firstVisitQuestions: [],
        postOpQuestions: [],
        postOpQuestionsExist: false,
        firstVisitQuestionsExist: false,
        tags: []
      };
      this.showExamDialog = false;
      this.messages = [];
      this.clearLocalStorage();
    },

    clearLocalStorage() {
      localStorage.removeItem('messageCache')
      localStorage.removeItem('currentPatient')
      localStorage.removeItem('patient_id')
    },

    submitPractice() {
      this.currentPatient.patient._id = this.currentPatient._id
      this.currentPatient.endTime = new Date()
      if (this.currentPatient.startTime) {
        this.currentPatient.duration = (this.currentPatient.endTime - this.currentPatient.startTime) / 1000
      } else {
        this.currentPatient.duration = 0
      }
      this.currentPatient.messages = this.messages

      ExamAnswerService.add(this.currentPatient).then((res) => {
        console.log(res)
        this.showExamDialog = false
        this.messages = []
        this.clearLocalStorage()
        this.getPracticePatientWithGroupByStudent()
      })
        .catch((err) => {
          console.error(err)
        })
    },

    returnImageUrl(imageUrl) {
      return baseUrl + "uploadFiles/backgroundImage/" + imageUrl
    },

    returnClinicFileUrl(imageUrl) {
      return baseUrl + "uploadFiles/clinicFiles/" + imageUrl
    },

    returnFirstVisitFileUrl(imageUrl) {
      return baseUrl + "uploadFiles/firstVisitFiles/" + imageUrl
    },

    returnPostOpFileUrl(imageUrl) {
      return baseUrl + "uploadFiles/postOpFiles/" + imageUrl
    },

    updateAnswer(questionIndex, answer) {
      if (this.currentStep === 2) {
        this.currentPatient.firstVisitQuestions[questionIndex].answer = answer
      } else if (this.currentStep === 3) {
        this.currentPatient.postOpQuestions[questionIndex].answer = answer
      }
      this.saveTesttoLocalStorage()
    },
    async toggleRecording() {
      try {
        // 只處理停止錄音
        if (this.isRecording) {
          this.isRecording = false
          this.isProcessing = true
          
          const audioBlob = await RecorderService.stopRecording()
          const result = await AudioService.speechToText(audioBlob, this.currentPatient._id)
          
          // 更新對應的文字欄位
          if (result && result.text_result) {
            // 根據目標欄位更新不同的值
            if (this.currentRecordingTarget === 'messageInput') {
              this.messageInput = (this.messageInput || '') + (this.messageInput ? ' ' : '') + result.text_result
            } else if (this.currentRecordingTarget.includes('firstVisitQuestions')) {
              const currentAnswer = this.currentPatient.firstVisitQuestions[this.currentFirstVisitQuestionIndex].answer || ''
              this.currentPatient.firstVisitQuestions[this.currentFirstVisitQuestionIndex].answer = 
                currentAnswer + (currentAnswer ? ' ' : '') + result.text_result
            } else if (this.currentRecordingTarget.includes('postOpQuestions')) {
              const currentAnswer = this.currentPatient.postOpQuestions[this.currentPostOpQuestionIndex].answer || ''
              this.currentPatient.postOpQuestions[this.currentPostOpQuestionIndex].answer = 
                currentAnswer + (currentAnswer ? ' ' : '') + result.text_result
            }
            
            // 儲存更新到 localStorage
            this.saveTesttoLocalStorage()
            
            // 關閉對話框
            setTimeout(() => {
              this.isProcessing = false
              this.showRecordingDialog = false
              this.currentRecordingTarget = null
            }, 500) // 短暫延遲以顯示處理完成
          }
        }
      } catch (error) {
        console.error('Voice input error:', error)
        this.recordingError = '語音輸入發生錯誤'
        this.isRecording = false
        this.isProcessing = false
      }
    },
    cancelRecording() {
      if (this.isRecording) {
        RecorderService.stopRecording()
        this.isRecording = false
      }
      this.isProcessing = false
      this.showRecordingDialog = false
      this.currentRecordingTarget = null
      this.recordingError = ''
    },
    viewScoreSheet(result) {
      this.examData = {
        exam: {
          name: result.exam?.name || '練習測驗',
          duration: result.duration || 0
        },
        student: {
          name: result.student?.name || localStorage.getItem('name')
        },
        examScore: result.examScore || 0,
        examDuration: result.duration || 0,
        examResult: {
          result: result.result || 0,
          comment: result.comment || ''
        },
        teacher: {
          name: result.teacher?.name || '考官'
        },
        examResultDetail: result.examResultDetail || []
      };
      this.showScoreSheet = true;
    },
    showPracticeResultsDialog(patient) {
      this.practiceResults = patient.examResults
      console.log(this.practiceResults)
      this.showPracticeResults = true
    },
    formatDateTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hour}:${minute}`;
    },
    exportPracticeResultsCsv() {
      // 準備 CSV 內容
      const headers = [
        '完成時間',
        '學生姓名',
        '分數'
      ];

      let csvContent = headers.join(',') + '\n';

      // 添加每個練習的資料
      this.practiceResults.forEach(result => {
        const row = [
          this.formatDateTime(result.uploadTime),
          result.student?.name || '',
          result.examScore || 0
        ].map(field => `"${field}"`).join(',');

        csvContent += row + '\n';
      });

      // 添加 BOM 以確保 Excel 正確顯示中文
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = `練習結果_${this.selectedPatient?.name || '未命名'}_${new Date().toLocaleDateString()}.csv`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    showAnswerDetail(result) {
      ExamResultService.teacherGetExamResultDetail(result._id).then((res) => {
        this.selectedExamResult = res.data.data.patient;
        this.studentAnswer = {
          messages: res.data.data.exam_result_id.messages,
          answers: res.data.data.exam_result_id.answerData[0]
        };
        this.showAnswerViewer = true;
      });
    },
    cleanupAvatar() {
      if (this.avatarSynthesizer) {
        this.avatarSynthesizer.close();
      }
      if (this.peerConnection) {
        this.peerConnection.close();
      }
      this.isAvatarReady = false;
      this.isAvatarVideoLoaded = false;
      this.isRTCConnecting = false;
    },
    async testAvatarSpeak() {
      if (!this.isAvatarReady || this.isSpeaking) return;
      const testText = '你好，這是一個測試。';
      if (!testText || typeof testText !== 'string' || testText.trim() === '') {
        console.warn('[Avatar] 測試語音內容為空，略過');
        return;
      }
      try {
        this.isSpeaking = true;
        console.log('Testing avatar speech...');
        if (this.avatarSynthesizer) {
          await this.avatarSynthesizer.speakTextAsync(testText);
          console.log('Speech completed');
        } else {
          console.error('Avatar synthesizer not initialized');
        }
      } catch (error) {
        console.error('Error in test speech:', error);
      } finally {
        this.isSpeaking = false;
      }
    },
    updateAudioVolume(value) {
      const audioElement = document.getElementById('avatarAudio');
      if (audioElement) {
        audioElement.volume = value;
      }
    },
    onAvatarVideoLoaded() {
      this.isAvatarVideoLoaded = true;
      this.isRTCConnecting = false;
      this.showRTCLoading = false;
      console.log('[RTC] onAvatarVideoLoaded fired. isAvatarVideoLoaded:', this.isAvatarVideoLoaded, 'isRTCConnecting:', this.isRTCConnecting);
      console.log('[RTC] onAvatarVideoLoaded called!');
    },
    handleVoiceInput(target) {
      // target 可能是 'messageInput' 或其他欄位
      this.currentRecordingTarget = target;
      this.showRecordingDialog = true;
      this.isRecording = true;
      this.isProcessing = false;
      this.recordingError = '';
      // 開始錄音
      if (typeof RecorderService !== 'undefined' && RecorderService.startRecording) {
        RecorderService.startRecording().catch((err) => {
          this.alert('無法啟動錄音，請檢查麥克風權限'+err)
          this.recordingError = '無法啟動錄音，請檢查麥克風權限';
          this.isRecording = false;
          this.isProcessing = false;
        });
      } else {
        this.recordingError = '錄音服務未正確載入';
        this.isRecording = false;
        this.isProcessing = false;
      }
    },
    cancelResumePractice() {
      this.clearLocalStorage();
      this.showResumePracticeBtn = false;
      this.currentPatient = {
        name: "",
        gender: "",
        age: "",
        description: "",
        taskDescription: "",
        backgroundImage: "",
        clinicFiles: [],
        firstVisitFiles: [],
        postOpFiles: [],
        firstVisitQuestions: [],
        postOpQuestions: [],
        postOpQuestionsExist: false,
        firstVisitQuestionsExist: false,
        tags: []
      };
      this.messages = [];
    },
  },

  computed: {
    hasNextStep() {
      if (this.currentStep === 1) {
        return this.currentPatient.firstVisitQuestionsExist || this.currentPatient.postOpQuestionsExist;
      }
      if (this.currentStep === 2) {
        return this.currentPatient.postOpQuestionsExist;
      }
      return false;
    },

    isLastStep() {
      if (this.currentStep === 1) {
        return !this.currentPatient.firstVisitQuestionsExist && !this.currentPatient.postOpQuestionsExist;
      }
      if (this.currentStep === 2) {
        return !this.currentPatient.postOpQuestionsExist;
      }
      return this.currentStep === 3;
    }
  },

  beforeDestroy() {
    this.cleanupAvatar();
  }
}
</script>

<style scoped>
/* 全局容器樣式 */
.v-container {
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* 病患卡片樣式 */
.patient-card,
.osce-patient-card {
  position: relative;
  height: 250px;
  overflow: hidden;
  margin-bottom: 16px;
  transition: all 0.3s;
}

.patient-card .v-card__text,
.osce-patient-card .v-card__text {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.patient-card-background,
.osce-patient-card__background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.patient-card-content,
.osce-patient-card__content {
  position: relative;
  z-index: 1;
  background: rgba(0, 0, 0, 0.5);
  height: 100%;
}

/* 病患信息樣式 */
.patient-info,
.osce-patient-info,
.patient-info-card,
.osce-patient-info-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.patient-info-card,
.osce-patient-info-card {
  place-content: end;
}

.chief-complaint,
.osce-chief-complaint {
  /* flex: 1; */
  display: flex;
  flex-direction: column;
  /* height: 100%; */
}

.chief-complaint .v-textarea {
  max-height: 200px;
  overflow-y: auto;
}

/* 對話框和頁面容器樣式 */
.exam-dialog,
.osce-exam-dialog {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f8f8f8;
  overflow: hidden;
  padding: 16px;
}

.progress-container,
.osce-progress-container {
  display: flex;
  align-items: center;
  padding: 12px 24px;
  background-color: #fff;
  border-bottom: 1px solid #eee;
  margin-bottom: 16px;
}

.content-container,
.osce-content-container {
  display: grid;
  grid-template-columns: 30% 70%;
  gap: 24px;
  padding: 24px;
  height: calc(100vh - 64px);
  overflow: hidden;
}

/* 左側虛擬人影片區域 */
.patient-section {
  height: 90%;
}

.video-section {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.video-placeholder {
  flex: 1;
  position: relative;
  background-color: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* 右側區域 */
.exam-form-section {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 24px;
}

/* 上半部：病患資料和圖片 */
.top-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  height: 40%;
}

.patient-info-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-group {
  flex: 1;
}

.chief-complaint {
  /* flex: 1; */
  display: flex;
  flex-direction: column;
  /* height: 100%; */
}

/* 圖片區域 */
.image-section {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.image-section_face {
  height: 60%;
  display: flex;
  flex-direction: column;
}

.image-slider-container {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.image-slider-container_face {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 100%;
  overflow-y: auto;
  max-height: 550px;
}


.image-scroll-container {
  display: flex;
  flex-direction: row  ;
  overflow-x: auto;
  max-width: 100%;
  scroll-behavior: smooth;
  height: 100%;
}

.image-scroll-container_face {
  display: flex;
  flex-direction: row  ;
  overflow-x: auto;
  max-width: 100%;
  scroll-behavior: smooth;
  margin: 0px auto;
}



.image-wrapper {
  flex: 0 0 auto;
  width: 180px;
  height: 120px;
  margin-right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Optional: add a border or background for better visibility */
}

.image-wrapper_face {
  flex: 0 0 auto;
  width: 360px;
  height: 210px;
  margin: 10px auto;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Optional: add a border or background for better visibility */
}

.image-wrapper .v-img,
.image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: contain; /* or 'cover' if you want to fill the box */
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

/* 下半部：聊天室 */
.chat-section {
  height: auto;
  max-height: 400px;
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

.chat-container {
  height: 100%;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #eee;
}

.messages-container {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.input-container {
  padding: 16px;
  border-top: 1px solid #eee;
}

/* 聊天泡泡樣式修正 */
.message {
  max-width: 70%;
  padding: 8px 12px;
  margin: 4px 0;
  border-radius: 8px;
  word-break: break-word;
  font-size: 20px;
  line-height: 1.5;
  display: inline-block;
}

.message-sent {
  align-self: flex-end;
  background-color: #000;
  color: #fff;
  text-align: right;
}

.message-received {
  align-self: flex-start;
  background-color: #f5f5f5;
  color: #333;
  text-align: left;
}

/* 其他樣式保持不變 */
.slider-arrow,
.osce-slider-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  background: rgba(255,255,255,0.8);
  border-radius: 50%;
}

.slider-arrow.left,
.osce-slider-arrow--left {
  left: 0;
}

.slider-arrow.right,
.osce-slider-arrow--right {
  right: 0;
}

.video-loading-text,
.osce-video-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #666;
  font-size: 16px;
  z-index: 1;
  background: rgba(255,255,255,0.7);
  padding: 8px 16px;
  border-radius: 8px;
}

.rtc-loading-overlay,
.osce-rtc-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255,255,255,0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.rtc-loading-tip,
.osce-rtc-loading__tip {
  margin-top: 16px;
  color: #333;
  font-size: 18px;
}

/* 進度選擇器當前步驟樣式修正 */
.progress-btn.active {
  background-color: #000 !important;
  color: #fff !important;
  border-radius: 8px;
  font-weight: bold;
}

/* 打字指示器動畫修正 */
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 8px 16px;
  margin-top: 8px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #666;
  border-radius: 50%;
  animation: bounce 1s infinite;
  display: inline-block;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}
.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

/* 初診題目切換器樣式修正 */
.questions-answer-container {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 24px;
  border-top: 1px solid #e0e0e0;
  padding-top: 24px;
  min-height: 300px;
}

.questions-list {
  border-right: 1px solid #e0e0e0;
  padding-right: 16px;
  min-width: 120px;
}

.nav-item {
  padding: 12px 16px;
  cursor: pointer;
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
  border-radius: 4px;
  transition: background 0.2s;
}

.nav-item.active {
  color: #000;
  font-weight: 500;
  background: #f0f0f0;
}

.nav-arrows {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
}

/* 強制 loading Dialog 永遠在最上層 */
.v-dialog.v-dialog--active[style*='width: 300px'] {
  z-index: 9999 !important;
}
</style>
